import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Heart,
  ShoppingBag,
  Menu,
  X,
  User,
  Gem,
  Package2,
  Sparkles,
  BookOpen,
  PackageOpen,
  Waves,
  ChevronDown,
  ArrowRight,
  Package,
} from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { NAV_LINKS, PICK_YOUR_SET_BOXES } from "@/data/products";
import GroveLogo from "./GroveLogo";
import { GiftHighlight } from "@/components/MiniGiftBox";

const AnnouncementBar = () => (
  <div
    className="w-full text-center text-[11px] tracking-[0.18em] uppercase py-2 px-4"
    style={{ background: "#4A2C35", color: "#FDF6F0" }}
    data-testid="announcement-bar"
  >
    Free shipping on orders above ₹499 · COD available Pan India
  </div>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobilePickOpen, setMobilePickOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const megaCloseTimer = useRef(null);
  const { cartCount, wishlist, setDrawerOpen } = useStore();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Small delay before close so users can move mouse into the dropdown
  const openMega = () => {
    if (megaCloseTimer.current) clearTimeout(megaCloseTimer.current);
    setMegaOpen(true);
  };
  const scheduleCloseMega = () => {
    if (megaCloseTimer.current) clearTimeout(megaCloseTimer.current);
    megaCloseTimer.current = setTimeout(() => setMegaOpen(false), 140);
  };

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <AnnouncementBar />
      <div
        className={`transition-all duration-300 ${
          scrolled
            ? "backdrop-blur-xl bg-cream/80 border-b border-sectionbreak"
            : "bg-cream/60"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
          <div
            className={`flex items-center justify-between transition-all duration-300 ${
              scrolled ? "h-20" : "h-24"
            }`}
          >
            <button
              className="lg:hidden p-2 -ml-2 text-plum"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-trigger"
            >
              <Menu size={22} />
            </button>

            <Link
              to="/"
              className="flex items-center gap-2 text-plum"
              data-testid="logo-link"
            >
              <GroveLogo height={scrolled ? 48 : 60} />
            </Link>

            <nav
              className="hidden lg:flex items-center gap-5 xl:gap-7 flex-1 justify-center px-4"
              data-testid="primary-nav"
            >
              {NAV_LINKS.map((link) => {
                const Icon =
                  link.icon === "Gem"
                    ? Gem
                    : link.icon === "Package2"
                      ? Package2
                      : link.icon === "Sparkles"
                        ? Sparkles
                        : link.icon === "BookOpen"
                          ? BookOpen
                          : link.icon === "PackageOpen"
                            ? PackageOpen
                            : link.icon === "Waves"
                              ? Waves
                              : null;
                const isPickYourSet = link.label === "Pick Your Set";

                const linkEl = (
                  <Link
                    key={link.label}
                    to={link.to}
                    className="inline-flex items-center gap-1.5 text-[13px] xl:text-[14px] font-medium tracking-[0.02em] text-plum/85 hover:text-blush transition-colors whitespace-nowrap"
                    data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {Icon && (
                      <Icon
                        size={15}
                        strokeWidth={1.9}
                        className="text-gold"
                      />
                    )}
                    {link.label}
                    {isPickYourSet && (
                      <ChevronDown
                        size={13}
                        strokeWidth={2}
                        className={`opacity-60 transition-transform duration-300 ${
                          megaOpen ? "rotate-180" : ""
                        }`}
                      />
                    )}
                  </Link>
                );

                if (isPickYourSet) {
                  return (
                    <div
                      key={link.label}
                      className="relative"
                      onMouseEnter={openMega}
                      onMouseLeave={scheduleCloseMega}
                      data-testid="mega-menu-trigger"
                    >
                      {linkEl}
                    </div>
                  );
                }
                return linkEl;
              })}
            </nav>

            <div className="flex items-center gap-1 sm:gap-3 text-plum">
              <button
                className="p-2 hover:text-blush transition-colors"
                aria-label="Search"
                data-testid="search-button"
              >
                <Search size={19} />
              </button>
              <Link
                to="/wishlist"
                className="hidden sm:flex p-2 hover:text-blush transition-colors relative"
                aria-label="Wishlist"
                data-testid="wishlist-button"
              >
                <Heart size={19} />
                {wishlist.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-blush text-cream text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-medium">
                    {wishlist.length}
                  </span>
                )}
              </Link>
              <button
                className="hidden sm:flex p-2 hover:text-blush transition-colors"
                aria-label="Account"
                data-testid="account-button"
              >
                <User size={19} />
              </button>
              <button
                className="p-2 hover:text-blush transition-colors relative"
                aria-label="Cart"
                onClick={() => setDrawerOpen(true)}
                data-testid="cart-button"
              >
                <ShoppingBag size={19} />
                {cartCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 bg-blush text-cream text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-medium"
                    data-testid="cart-count"
                  >
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Desktop Mega-menu (hover) — June-style editorial dropdown ─── */}
      <div
        className={`hidden lg:block absolute left-1/2 -translate-x-1/2 top-full z-40 transition-all duration-300 ${
          megaOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
        onMouseEnter={openMega}
        onMouseLeave={scheduleCloseMega}
        data-testid="mega-menu-panel"
        style={{ width: "min(900px, 94vw)" }}
      >
        <div
          className="mt-3 rounded-[24px] overflow-y-auto overscroll-contain max-h-[calc(100vh-140px)] shadow-[0_36px_70px_-28px_rgba(74,44,53,0.42)]"
          style={{
            background: "#FDF6F0",
            border: "1px solid rgba(197,152,90,0.42)",
          }}
        >
          {/* Header strip */}
          <div
            className="px-7 py-4 flex items-center justify-between border-b"
            style={{ borderColor: "rgba(197,152,90,0.25)" }}
          >
            <p
              className="text-[10px] tracking-[0.42em] uppercase font-semibold flex items-center gap-3"
              style={{ color: "#C5985A" }}
            >
              <span aria-hidden>✦</span> Pick Your Perfect Set
            </p>
            <Link
              to="/#pick-your-set"
              onClick={() => setMegaOpen(false)}
              className="text-[10.5px] tracking-[0.28em] uppercase font-semibold text-plum/70 hover:text-blush inline-flex items-center gap-1.5 transition-colors"
              data-testid="mega-menu-see-all"
            >
              See All <ArrowRight size={12} strokeWidth={2.4} />
            </Link>
          </div>

          {/* Three editorial mini cards — themed per box */}
          <div className="grid grid-cols-3 gap-4 p-5">
            {PICK_YOUR_SET_BOXES.map((box) => {
              const t = box.theme;
              return (
                <Link
                  key={box.id}
                  to={box.to}
                  onClick={() => setMegaOpen(false)}
                  className={`group relative flex flex-col rounded-[18px] overflow-hidden border transition-[transform,box-shadow] hover:shadow-[0_20px_38px_-22px_rgba(74,44,53,0.55)] hover:-translate-y-0.5 ${t.dark ? "ring-1 ring-gold/50" : ""}`}
                  style={{ background: t.bg, borderColor: t.border }}
                  data-testid={`mega-menu-${box.id}`}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={box.image}
                      alt={box.label}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
                      loading="lazy"
                      draggable="false"
                    />
                  </div>
                  <div className="relative p-4 pt-5 flex flex-col items-center text-center gap-1.5 flex-1">
                    {box.ribbon && (
                      <span
                        className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap px-2.5 py-1 rounded-full text-[8.5px] tracking-[0.24em] uppercase font-semibold text-plum shadow-md"
                        style={{ background: "linear-gradient(135deg,#F4D58D 0%,#E0BC76 55%,#C5985A 100%)" }}
                        data-testid={`mega-menu-${box.id}-ribbon`}
                      >
                        {box.ribbon}
                      </span>
                    )}
                    <p className="text-[10px] tracking-[0.32em] uppercase font-semibold" style={{ color: t.accent }}>
                      {box.label}
                    </p>
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-serif leading-none text-[26px]" style={{ color: t.text }}>
                        {box.price}
                      </span>
                      <span className="text-[9px] tracking-[0.18em] uppercase opacity-60" style={{ color: t.text }}>
                        only
                      </span>
                    </div>
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] tracking-[0.2em] uppercase font-semibold"
                      style={{ background: t.pill, border: `1px solid ${t.border}`, color: t.dark ? t.accent : t.text }}
                    >
                      <Package size={9} strokeWidth={2.4} />
                      {box.pieces}
                    </span>
                    {box.gift && <GiftHighlight text={box.gift} testId="mega-menu-dream-gift" />}
                    <span
                      className="inline-flex items-center gap-1 mt-auto pt-1.5 text-[10px] tracking-[0.26em] uppercase font-semibold transition-colors"
                      style={{ color: t.dark ? t.accent : t.text }}
                    >
                      Build Your Box
                      <ArrowRight size={12} strokeWidth={2.4} className="transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Footer line */}
          <p
            className="pb-4 text-center text-[9.5px] tracking-[0.32em] uppercase font-semibold text-plum/55"
            data-testid="mega-menu-footer"
          >
            <span className="text-gold" aria-hidden>✦</span> Free Shipping · COD
            Available · Easy Returns{" "}
            <span className="text-gold" aria-hidden>✦</span>
          </p>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`fixed inset-0 bg-cream z-[60] transition-transform duration-400 lg:hidden flex flex-col ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="mobile-menu"
      >
        <div className="flex items-center justify-between px-5 h-20 border-b border-sectionbreak flex-shrink-0">
          <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2">
            <GroveLogo height={42} />
          </Link>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 text-plum"
            aria-label="Close menu"
            data-testid="mobile-menu-close"
          >
            <X size={24} />
          </button>
        </div>
        <nav className="flex flex-col px-6 py-4 gap-0 overflow-y-auto flex-1">
          {NAV_LINKS.map((link, idx) => {
            const isPickYourSet = link.label === "Pick Your Set";
            const baseStyle = {
              opacity: mobileOpen ? 1 : 0,
              transform: mobileOpen ? "translateY(0)" : "translateY(10px)",
              transition: `opacity 400ms ease ${idx * 40}ms, transform 400ms ease ${idx * 40}ms`,
            };

            if (isPickYourSet) {
              return (
                <div
                  key={link.label}
                  className="border-b border-sectionbreak/60"
                  style={baseStyle}
                >
                  <button
                    type="button"
                    onClick={() => setMobilePickOpen((v) => !v)}
                    className="w-full flex items-center justify-between font-serif text-[22px] text-plum py-2.5 text-left"
                    aria-expanded={mobilePickOpen}
                    data-testid="mobile-nav-pick-your-set-toggle"
                  >
                    <span>{link.label}</span>
                    <ChevronDown
                      size={18}
                      className={`transition-transform duration-300 ${
                        mobilePickOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div
                    className="overflow-hidden transition-[max-height,opacity] duration-400 ease-out"
                    style={{
                      maxHeight: mobilePickOpen ? "600px" : "0px",
                      opacity: mobilePickOpen ? 1 : 0,
                    }}
                  >
                    <div className="grid grid-cols-2 gap-3 pb-4 pt-1">
                      {PICK_YOUR_SET_BOXES.map((box) => (
                        <Link
                          key={box.id}
                          to={box.to}
                          onClick={() => {
                            setMobileOpen(false);
                            setMobilePickOpen(false);
                          }}
                          className="group relative overflow-hidden rounded-[14px] bg-cream border border-gold/25 flex flex-col"
                          data-testid={`mobile-pick-${box.id}`}
                        >
                          <div className="relative aspect-square overflow-hidden bg-pearl">
                            <img
                              src={box.image}
                              alt={box.label}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            {box.ribbon && (
                              <span className="absolute top-2 left-2 text-[8px] tracking-[0.2em] uppercase bg-plum text-cream px-2 py-1 rounded-full">
                                {box.ribbon}
                              </span>
                            )}
                          </div>
                          <div className="p-3 flex-1 flex flex-col">
                            <span className="text-[9px] tracking-[0.28em] uppercase text-blush font-semibold">
                              {box.offerPill}
                            </span>
                            <p className="font-serif text-plum text-[16px] leading-tight mt-1">
                              {box.label}
                            </p>
                            <p className="text-[10px] text-plum/60 mt-0.5">
                              {box.pieces}
                            </p>
                            <p className="text-[13px] font-semibold text-plum mt-1.5">
                              {box.price}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={link.label}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className="font-serif text-[22px] text-plum py-2.5 border-b border-sectionbreak/60"
                style={baseStyle}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-6 py-4 flex items-center gap-3 text-plum/70 text-[11px] tracking-wide uppercase border-t border-sectionbreak/60 flex-shrink-0">
          <span>Free shipping ₹499+</span>
          <span>·</span>
          <span>COD available</span>
        </div>
      </div>
    </header>
  );
};

export default Header;
